/**
 * component-event-main.js
 * Ticket selector: timeslot (pills) and tiers (qty counter) modes.
 * Works with variants created by Guest Manager.
 */

(function () {
  'use strict';

  const form = document.getElementById('event-ticket-form');
  if (!form) return;

  const selectorStyle = form.dataset.selectorStyle || 'timeslot';
  const submitBtn     = document.getElementById('event-ticket-submit');
  const waitlistBtn   = document.getElementById('event-waitlist-trigger');

  function priceLabel(variantId) {
    const labels = window.EventProduct?.priceLabels || {};
    return labels[String(variantId)] || '';
  }

  const CRITICAL_THRESHOLD = 5;
  const LOW_THRESHOLD = 10;

  function stockMessageFor(variant) {
    const stock = (window.EventProduct?.stock || {})[String(variant.id)];
    if (!variant.available || !stock || !stock.management || stock.policy === 'continue') return '';

    const qty = Number(stock.quantity);
    if (!Number.isFinite(qty) || qty <= 0) return '';
    if (qty <= CRITICAL_THRESHOLD) return `Only ${qty} left!`;
    if (qty <= LOW_THRESHOLD) return 'Hurry, low in stock';
    return '';
  }

  function applyVariantState(variant) {
    if (!variant || !submitBtn) return;
    const soldOut = !variant.available;
    const price = priceLabel(variant.id);

    submitBtn.disabled = soldOut;
    submitBtn.textContent = soldOut
      ? (price ? `Sold Out \u2013 ${price}` : 'Sold Out')
      : 'Get Tickets';

    if (waitlistBtn) waitlistBtn.classList.toggle('event-ticket__bis--isVisible', soldOut);

    const stockEl = document.getElementById('event-stock-message');
    if (stockEl) stockEl.textContent = stockMessageFor(variant);
  }

  const errorEl = document.getElementById('event-cart-error');

  function showCartError(message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function clearCartError() {
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  function addToCart(payload, onDone) {
    clearCartError();
    const originalText = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Adding\u2026';

    function restore() {
      submitBtn.textContent = originalText;
      submitBtn.disabled = false;
    }

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(r => r.json().then(body => ({ ok: r.ok, body })))
      .then(({ ok, body }) => {
        restore();
        if (!ok) {
          showCartError(body.description || body.message || 'This ticket is no longer available.');
          if (onDone) onDone(false);
          return;
        }
        if (onDone) onDone(true);
        document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
      })
      .catch(() => {
        restore();
        showCartError('Something went wrong. Please try again.');
        if (onDone) onDone(false);
      });
  }

  /* Collect line item properties (e.g. Venue / Address) from hidden inputs
     so they travel to the Shopify order and onto the Guest Manager ticket. */
  function collectProperties() {
    const props = {};
    form.querySelectorAll('input[name^="properties["]').forEach(input => {
      const match = input.name.match(/^properties\[(.+)\]$/);
      if (match && input.value) props[match[1]] = input.value;
    });
    return props;
  }

  /* ============================================================
     TIMESLOT MODE
     - option[0] = time slot pills
     - option[1] = ticket type pills (optional)
     - resolves to a single variant → standard form submit
  ============================================================ */
  if (selectorStyle === 'timeslot') {
    const variantInput  = document.getElementById('event-variant-id');
    const variants      = window.EventProduct?.variants || [];

    let selectedOptions = {};

    /* pre-select first value of each option */
    const slotPills = form.querySelectorAll('.event-ticket__timeslot-pill');
    const typePills = form.querySelectorAll('.event-ticket__type-pill');

    if (slotPills.length) selectedOptions[0] = slotPills[0].dataset.value;
    if (typePills.length) selectedOptions[1] = typePills[0].dataset.value;

    resolveVariant();

    /* pill click handler */
    form.addEventListener('click', e => {
      const pill = e.target.closest('.event-ticket__pill');
      if (!pill || pill.disabled) return;

      const idx = parseInt(pill.dataset.optionIndex, 10);
      const val = pill.dataset.value;

      /* deselect siblings */
      form.querySelectorAll(`.event-ticket__pill[data-option-index="${idx}"]`).forEach(p => {
        p.classList.remove('is-selected');
        p.setAttribute('aria-pressed', 'false');
      });

      pill.classList.add('is-selected');
      pill.setAttribute('aria-pressed', 'true');
      selectedOptions[idx] = val;

      resolveVariant();
    });

    function resolveVariant() {
      const match = variants.find(v => {
        const o0 = selectedOptions[0];
        const o1 = selectedOptions[1];
        if (o0 && o1) return v.option1 === o0 && v.option2 === o1;
        if (o0)       return v.option1 === o0;
        return true;
      });

      if (match) {
        variantInput.value = match.id;
        applyVariantState(match);
      }
    }

    /* AJAX add to cart */
    form.addEventListener('submit', e => {
      e.preventDefault();
      if (!variantInput.value) return;

      const payload = { id: parseInt(variantInput.value, 10), quantity: 1 };
      const props = collectProperties();
      if (Object.keys(props).length) payload.properties = props;

      addToCart(payload, () => resolveVariant());
    });
  }

  /* ============================================================
     TIERS MODE
     - each variant shown as a row with +/- quantity counter
     - clicking submit posts all variants with qty > 0 to cart
  ============================================================ */
  if (selectorStyle === 'tiers') {
    const inputs    = form.querySelectorAll('.event-ticket__qty-input');

    /* max comes from the markup, where Liquid caps it at the tracked inventory */
    function maxFor(input) {
      const max = parseInt(input.getAttribute('max'), 10);
      return isNaN(max) ? 10 : max;
    }

    function clampQty(input, value) {
      let n = parseInt(value, 10);
      if (isNaN(n) || n < 0) n = 0;
      const max = maxFor(input);
      if (n > max) n = max;
      return n;
    }

    function totalQty() {
      let total = 0;
      inputs.forEach(input => { total += clampQty(input, input.value); });
      return total;
    }

    const allSoldOut = (window.EventProduct?.variants || []).every(v => !v.available);

    function updateSubmitState() {
      if (!submitBtn) return;
      if (allSoldOut) {
        submitBtn.disabled = true;
        return;
      }
      submitBtn.disabled = totalQty() === 0;
    }

    inputs.forEach(input => {
      const tier     = input.closest('.event-ticket__tier');
      const minusBtn = tier.querySelector('.event-ticket__qty-minus');
      const plusBtn  = tier.querySelector('.event-ticket__qty-plus');

      function syncStepper() {
        if (input.disabled || !plusBtn) return;
        plusBtn.disabled = clampQty(input, input.value) >= maxFor(input);
      }

      if (plusBtn) plusBtn.addEventListener('click', () => {
        input.value = clampQty(input, clampQty(input, input.value) + 1);
        syncStepper();
        updateSubmitState();
      });

      if (minusBtn) minusBtn.addEventListener('click', () => {
        input.value = clampQty(input, clampQty(input, input.value) - 1);
        syncStepper();
        updateSubmitState();
      });

      /* allow typing, clamp to [0, max] on change/blur */
      input.addEventListener('input', updateSubmitState);
      input.addEventListener('change', () => {
        input.value = clampQty(input, input.value);
        syncStepper();
        updateSubmitState();
      });

      syncStepper();
    });

    updateSubmitState();

    form.addEventListener('submit', e => {
      e.preventDefault();

      const props = collectProperties();
      const hasProps = Object.keys(props).length > 0;
      const items = [];
      inputs.forEach(input => {
        const qty = clampQty(input, input.value);
        if (qty > 0) {
          const item = { id: parseInt(input.dataset.variantId, 10), quantity: qty };
          if (hasProps) item.properties = props;
          items.push(item);
        }
      });

      if (!items.length) return;

      addToCart({ items }, () => updateSubmitState());
    });
  }

  /* ============================================================
     SIMPLE MODE
     - single variant, quantity stepper feeds the hidden quantity input
  ============================================================ */
  if (selectorStyle === 'simple') {
    const qtyInput     = document.getElementById('qty-simple');
    const hiddenQty    = document.getElementById('event-qty-input');
    const variantInput = document.getElementById('event-variant-id');

    if (qtyInput && hiddenQty) {
      const minusBtn = form.querySelector('.event-ticket__qty-minus');
      const plusBtn  = form.querySelector('.event-ticket__qty-plus');
      const maxQty = parseInt(qtyInput.getAttribute('max'), 10) || 10;

      function clamp(value) {
        let n = parseInt(value, 10);
        if (isNaN(n) || n < 1) n = 1;
        if (n > maxQty) n = maxQty;
        return n;
      }

      function syncQty() {
        const n = clamp(qtyInput.value);
        qtyInput.value = n;
        hiddenQty.value = n;
        if (plusBtn && !qtyInput.disabled) plusBtn.disabled = n >= maxQty;
        if (minusBtn && !qtyInput.disabled) minusBtn.disabled = n <= 1;
      }

      if (minusBtn) minusBtn.addEventListener('click', () => {
        qtyInput.value = clamp(clamp(qtyInput.value) - 1);
        syncQty();
      });

      if (plusBtn) plusBtn.addEventListener('click', () => {
        qtyInput.value = clamp(clamp(qtyInput.value) + 1);
        syncQty();
      });

      qtyInput.addEventListener('change', syncQty);
      syncQty();
    }

    form.addEventListener('submit', e => {
      e.preventDefault();
      if (!variantInput || !variantInput.value || submitBtn.disabled) return;

      const payload = {
        id: parseInt(variantInput.value, 10),
        quantity: parseInt(hiddenQty ? hiddenQty.value : '1', 10) || 1
      };
      const props = collectProperties();
      if (Object.keys(props).length) payload.properties = props;

      addToCart(payload);
    });
  }

  /* ============================================================
     WAITLIST
     Sold-out events with is_waitlist enabled show a notify button that
     opens a modal and creates a Klaviyo back-in-stock subscription for
     the selected ticket variant.
  ============================================================ */
  (function initWaitlist() {
    const trigger = waitlistBtn;
    const modal   = document.getElementById('event-waitlist-modal');
    if (!trigger || !modal) return;

    const KLAVIYO_COMPANY_ID = 'SnBB9k';

    const waitlistForm = document.getElementById('event-waitlist-form');
    const emailInput   = document.getElementById('event-waitlist-email');
    const errorEl      = document.getElementById('event-waitlist-error');
    const successEl    = document.getElementById('event-waitlist-success');
    const submitEl     = waitlistForm.querySelector('button[type="submit"]');
    const ticketEl     = modal.querySelector('[js-waitlist-modal-ticket]');
    const ticketWrapEl = modal.querySelector('[js-waitlist-modal-ticket-wrap]');
    const submitText   = submitEl.textContent;

    /* the PDP modal names the variant as (size, color); here it is the ticket */
    function currentTicketLabel() {
      const variants = window.EventProduct?.variants || [];
      const id = targetVariantId();
      const variant = variants.find(v => String(v.id) === String(id));
      if (!variant || variant.title === 'Default Title') return '';
      return variant.title;
    }

    function open() {
      modal.hidden = false;
      document.body.classList.add('event-waitlist-open');
      const ticket = currentTicketLabel();
      if (ticketEl) ticketEl.textContent = ticket;
      if (ticketWrapEl) ticketWrapEl.hidden = !ticket;
      emailInput.focus();
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      modal.hidden = true;
      document.body.classList.remove('event-waitlist-open');
      document.removeEventListener('keydown', onKeydown);
      trigger.focus();
    }

    function onKeydown(e) {
      if (e.key === 'Escape') close();
    }

    function showError(message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    function targetVariantId() {
      const input = document.getElementById('event-variant-id');
      if (input && input.value) return input.value;
      const variants = window.EventProduct?.variants || [];
      return variants.length ? String(variants[0].id) : '';
    }

    trigger.addEventListener('click', open);
    modal.querySelectorAll('[data-waitlist-close]').forEach(el => {
      el.addEventListener('click', close);
    });

    waitlistForm.addEventListener('submit', e => {
      e.preventDefault();
      errorEl.hidden = true;

      const email = emailInput.value.trim();
      if (!email || !emailInput.checkValidity()) {
        showError('Please enter a valid email address.');
        emailInput.focus();
        return;
      }

      const variantId = targetVariantId();
      if (!variantId) {
        showError('Something went wrong. Please try again later.');
        return;
      }

      submitEl.disabled = true;
      submitEl.textContent = 'Signing you up\u2026';

      const payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email: email } } },
            channels: ['EMAIL']
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`
              }
            }
          }
        }
      };

      fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-06-15' },
        body: JSON.stringify(payload)
      })
        .then(response => {
          if (!response.ok) throw new Error('Subscription failed');
          waitlistForm.hidden = true;
          successEl.hidden = false;
        })
        .catch(() => {
          submitEl.disabled = false;
          submitEl.textContent = submitText;
          showError('There was a problem signing up. Please try again.');
        });
    });
  })();

})();
